import React, { useState } from "react";
import { Typography } from "@mui/material";
import { ChatInputContainer, StyledAutoGrowingInput, StyledChatInputBarContainer } from "./ChatInputBar.styled";
import ErrorSnackBar from "../../shared-components/errorsnackbar/ErrorSnackBar";
import FileAsImage from "./FileAsImage";
import { usePasteHandler } from "../hooks/usePastehandler";
import { useImageUpload } from "../hooks/useImageUpload";
import Attach from "./attachfile/Attach";
import Send from "./send/Send";
import { ChatMessage } from "../../types/Message.types";
import { useAuth } from '../../feature-auth/context/AuthContext';

interface ChatInputBarProps {
  onSendMessage: (
    message: ChatMessage,
    previousUserMessage?: ChatMessage,
    previousAIResponse?: ChatMessage
  ) => void;
  onHandleSendMessageWithImage: (message: ChatMessage) => void;
  isHomeView: boolean;
  messages: ChatMessage[];
  onCloseError: () => void;
}

const ChatInputBar: React.FC<ChatInputBarProps> = ({
  onSendMessage,
  onHandleSendMessageWithImage,
  isHomeView,
  messages,
  onCloseError,
}) => {
  const [userInputText, setUserInputText] = useState<string>("");
  const [userInputFile, setUserInputFile] = useState<File | null>(null);
  const [isStartChat, setIsStartChat] = useState<boolean>(true);
  const { handleImageUpload, error } = useImageUpload((validFile: File) => {
    setUserInputFile(validFile);
  });
  const { user } = useAuth();
  const displayName = user?.displayName || user?.email?.split('@')[0] || '';

  const handleRemoveImage = () => {
    setUserInputFile(null);
  };

  const handleStartChat = () => {
    const trimmed = userInputText.trim();

    if (trimmed && userInputFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result && typeof reader.result === "string") {
          const newMessage: ChatMessage = {
            fromUser: true,
            text: trimmed,
            imageUrl: reader.result,
          };
          onHandleSendMessageWithImage(newMessage);
        }
      };
      reader.readAsDataURL(userInputFile);
      setUserInputFile(null);
    } else if (trimmed) {
      const newMessage: ChatMessage = { fromUser: true, text: trimmed };
      onSendMessage(newMessage);
    }

    setUserInputText("");
    setIsStartChat(false);
  };


  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files ? event.target.files[0] : null;
    handleImageUpload(selectedFile);
  };

  usePasteHandler(handleImageUpload)

  return (
    <>
      {error && <ErrorSnackBar errorMessage={error} severity="error" onClose={onCloseError} />}
      <StyledChatInputBarContainer>
        {isHomeView && (
          <Typography variant="h5" sx={{ mb: 2 }} color="text.secondary">
            Hallo {displayName}, wie kann ich helfen?
          </Typography>
        )}
        <ChatInputContainer isStartChat={isStartChat} isUserInputFile={!!userInputFile}>
          <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: 8 }}>
            <div>
              {userInputFile && <FileAsImage file={userInputFile} onRemove={handleRemoveImage} />}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <StyledAutoGrowingInput
                placeholder="Stelle irgendeine Frage..."
                value={userInputText}
                onChange={(e) => setUserInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault(); // Zeilenumbruch verhindern
                    handleStartChat();
                  }
                }}
                minRows={1}
                maxRows={6}
                maxLength={2000}
              />
              <Attach handleFileInputChange={handleFileInputChange} />
              <Send handleStartChat={handleStartChat} isSendButtonDisabled={userInputText.length === 0} />
            </div>
          </div>
        </ChatInputContainer>
      </StyledChatInputBarContainer>
    </>
  );
};

export default ChatInputBar;